import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import CreateBinDialog from '../../components/BridgeClub/feed/CreateBinDialog';
import AddFeedDialog from '../../components/BridgeClub/feed/AddFeedDialog';
import { FEED_CATEGORIES } from '../../components/BridgeClub/feed/feedConstants';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import './BridgeClubFeed.css';

interface AnimalLink {
  id: number;
  animal_id: number;
  animal_name: string;
  animal_type: string;
  count: number;
  daily_consumption_lbs: number;
  effective_daily_lbs: number;
  system_estimate_lbs: number;
  use_custom_amount: boolean;
  custom_amount_per_day: number | null;
  custom_amount_unit: string;
  scoop_size_oz: number | null;
  feeding_notes: string;
}

interface Transaction {
  id: number;
  type: string;
  lbs_change: number;
  product_name: string;
  brand: string;
  created_at: string;
  notes: string;
}

interface FeedBin {
  id: number;
  name: string;
  feed_category: string;
  form_type: 'pellet' | 'mash' | 'whole' | null;
  form_type_display: string | null;
  feed_name_with_form: string;
  current_lbs: number;
  low_alert_lbs: number;
  critical_alert_lbs: number;
  total_daily_consumption: number;
  days_remaining: number | null;
  estimated_empty_date: string | null;
  status: 'good' | 'ok' | 'low' | 'critical' | 'empty' | 'unknown';
  fill_percentage: number;
  is_active: boolean;
  created_at: string;
  current_product_name: string | null;
  current_product_brand: string | null;
  animal_links: AnimalLink[];
  recent_transactions: Transaction[];
}

interface PendingAllocation {
  id: number;
  purchase_id: number | null;
  product_name: string;
  brand: string;
  is_red_bridge: boolean;
  lbs_per_unit: number;
  quantity: number;
  total_lbs: number;
  feed_category: string;
  suggested_bin_id: number | null;
  suggested_bin_name: string | null;
  created_at: string;
}

interface Animal {
  id: number;
  name: string;
  animal_type: string;
  category: string;
}

const BridgeClubFeed: React.FC = () => {
  const [bins, setBins] = useState<FeedBin[]>([]);
  const [pendingAllocations, setPendingAllocations] = useState<PendingAllocation[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [animals, setAnimals] = useState<Animal[]>([]);

  // Modal states
  const [showCreateBin, setShowCreateBin] = useState(false);
  const [showAddFeed, setShowAddFeed] = useState<FeedBin | null>(null);
  const [showAllocations, setShowAllocations] = useState(false);
  const [showBinDetail, setShowBinDetail] = useState<FeedBin | null>(null);
  const [showLinkAnimal, setShowLinkAnimal] = useState<FeedBin | null>(null);

  // Form states
  const [adjustLevel, setAdjustLevel] = useState('');
  // The bin detail folds the rare things away (Brad, 2026-09-22).
  const [showAdjust, setShowAdjust] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // RENAME (Brad, 2026-09-22: "I cant edit the name on the bins"). Null when not
  // editing; the draft name while the field is open.
  const [renaming, setRenaming] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Move animal confirmation state
  const [moveConfirm, setMoveConfirm] = useState<{
    animal: Animal;
    fromBin: FeedBin;
    toBin: FeedBin;
    fromLinkId: number;
  } | null>(null);

  // Delete bin confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<FeedBin | null>(null);

  // Custom feeding amount modal state
  const [editFeedingLink, setEditFeedingLink] = useState<{ bin: FeedBin; link: AnimalLink } | null>(null);
  const [feedingUseCustom, setFeedingUseCustom] = useState(false);
  const [feedingAmount, setFeedingAmount] = useState('');
  const [feedingUnit, setFeedingUnit] = useState('cups');
  const [feedingScoopSize, setFeedingScoopSize] = useState('');
  const [feedingNotes, setFeedingNotes] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [binsRes, animalsRes] = await Promise.all([
        bridgeClubAPI.getFeedBins(),
        bridgeClubAPI.getAnimals(),
      ]);

      const newBins = binsRes.bins || [];
      setBins(newBins);
      setPendingCount(binsRes.pending_allocations_count || 0);
      setAnimals(animalsRes || []);

      // Refresh showBinDetail if it's currently open
      setShowBinDetail(prev => {
        if (prev) {
          const updatedBin = newBins.find((b: FeedBin) => b.id === prev.id);
          return updatedBin || null;
        }
        return prev;
      });
    } catch (err) {
      console.error('Failed to load feed data:', err);
      setError('Failed to load feed tracking data');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingAllocations = async () => {
    try {
      const res = await bridgeClubAPI.getPendingAllocations();
      setPendingAllocations(res.pending || []);
      setShowAllocations(true);
    } catch (err) {
      console.error('Failed to load pending allocations:', err);
      setError('Failed to load pending items');
    }
  };

  const handleRename = async (bin: FeedBin) => {
    const name = (renaming || '').trim();
    if (!name || name === bin.name) { setRenaming(null); return; }
    setSaving(true);
    try {
      await bridgeClubAPI.updateFeedBin(bin.id, { name });
      setRenaming(null);
      setShowBinDetail({ ...bin, name });
      await loadData();
    } catch (err) {
      console.error('Failed to rename bin:', err);
      setError('Failed to rename the bin');
    } finally {
      setSaving(false);
    }
  };

  const handleAdjustLevel = async (bin: FeedBin) => {
    if (!adjustLevel) return;
    setSaving(true);
    try {
      await bridgeClubAPI.adjustBinLevel(bin.id, {
        new_level: parseFloat(adjustLevel),
        notes: 'Manual level adjustment',
      });
      setAdjustLevel('');
      setShowBinDetail(null);
      await loadData();
    } catch (err) {
      console.error('Failed to adjust level:', err);
      setError('Failed to adjust bin level');
    } finally {
      setSaving(false);
    }
  };

  const handleAllocate = async (alloc: PendingAllocation, binId: number) => {
    setSaving(true);
    try {
      await bridgeClubAPI.allocatePendingToBin(alloc.id, binId);
      // Remove from local state
      setPendingAllocations(prev => prev.filter(a => a.id !== alloc.id));
      setPendingCount(prev => Math.max(0, prev - 1));
      await loadData();
    } catch (err) {
      console.error('Failed to allocate:', err);
      setError('Failed to allocate feed to bin');
    } finally {
      setSaving(false);
    }
  };

  const handleSkipAllocation = async (alloc: PendingAllocation) => {
    setSaving(true);
    try {
      await bridgeClubAPI.skipPendingAllocation(alloc.id);
      setPendingAllocations(prev => prev.filter(a => a.id !== alloc.id));
      setPendingCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to skip:', err);
      setError('Failed to skip allocation');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateBinAndAllocate = async (alloc: PendingAllocation) => {
    setSaving(true);
    try {
      await bridgeClubAPI.createBinAndAllocate(alloc.id, {
        bin_name: `${alloc.product_name} Bin`,
        feed_category: alloc.feed_category,
      });
      setPendingAllocations(prev => prev.filter(a => a.id !== alloc.id));
      setPendingCount(prev => Math.max(0, prev - 1));
      await loadData();
    } catch (err) {
      console.error('Failed to create bin:', err);
      setError('Failed to create bin and allocate');
    } finally {
      setSaving(false);
    }
  };

  const handleLinkAnimal = async (bin: FeedBin, animalId: number) => {
    setSaving(true);
    try {
      await bridgeClubAPI.linkAnimalToBin(bin.id, { animal_id: animalId });
      setShowLinkAnimal(null);
      await loadData();
    } catch (err: any) {
      console.error('Failed to link animal:', err);
      setError(err?.error || 'Failed to link animal');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlinkAnimal = async (bin: FeedBin, linkId: number) => {
    setSaving(true);
    try {
      await bridgeClubAPI.unlinkAnimalFromBin(bin.id, linkId);
      await loadData();
    } catch (err) {
      console.error('Failed to unlink animal:', err);
      setError('Failed to unlink animal');
    } finally {
      setSaving(false);
    }
  };

  // Open the custom feeding edit modal
  const openEditFeedingModal = (bin: FeedBin, link: AnimalLink) => {
    setEditFeedingLink({ bin, link });
    setFeedingUseCustom(link.use_custom_amount);
    setFeedingAmount(link.custom_amount_per_day?.toString() || '');
    setFeedingUnit(link.custom_amount_unit || 'cups');
    setFeedingScoopSize(link.scoop_size_oz?.toString() || '');
    setFeedingNotes(link.feeding_notes || '');
  };

  // Save custom feeding settings
  const handleSaveFeeding = async () => {
    if (!editFeedingLink) return;
    setSaving(true);
    try {
      await bridgeClubAPI.updateBinAnimalFeeding(
        editFeedingLink.bin.id,
        editFeedingLink.link.id,
        {
          use_custom_amount: feedingUseCustom,
          custom_amount_per_day: feedingUseCustom && feedingAmount ? parseFloat(feedingAmount) : null,
          custom_amount_unit: feedingUnit,
          scoop_size_oz: feedingUnit === 'scoops' && feedingScoopSize ? parseFloat(feedingScoopSize) : null,
          feeding_notes: feedingNotes,
        }
      );
      setEditFeedingLink(null);
      await loadData();
    } catch (err) {
      console.error('Failed to update feeding:', err);
      setError('Failed to update feeding amount');
    } finally {
      setSaving(false);
    }
  };

  // Calculate preview of lbs conversion
  const getPreviewLbs = (): number => {
    if (!feedingUseCustom || !feedingAmount) {
      return editFeedingLink?.link.system_estimate_lbs || 0;
    }
    const amt = parseFloat(feedingAmount);
    switch (feedingUnit) {
      case 'lbs': return amt;
      case 'oz': return amt / 16;
      case 'cups': return amt * 0.25;
      case 'scoops': return (amt * (parseFloat(feedingScoopSize) || 8)) / 16;
      case 'grams': return amt / 453.592;
      case 'kg': return amt * 2.20462;
      default: return amt * 0.25;
    }
  };

  // Find which bin an animal is currently linked to
  const getAnimalCurrentBin = (animalId: number): { bin: FeedBin; linkId: number } | null => {
    for (const bin of bins) {
      const link = bin.animal_links.find(l => l.animal_id === animalId);
      if (link) {
        return { bin, linkId: link.id };
      }
    }
    return null;
  };

  // Handle clicking an animal in the Link Animal modal
  const handleAnimalClick = (animal: Animal, targetBin: FeedBin) => {
    const currentBinInfo = getAnimalCurrentBin(animal.id);

    // If already in target bin, do nothing (should be grayed out)
    if (currentBinInfo && currentBinInfo.bin.id === targetBin.id) {
      return;
    }

    // If in another bin, show confirmation
    if (currentBinInfo) {
      setMoveConfirm({
        animal,
        fromBin: currentBinInfo.bin,
        toBin: targetBin,
        fromLinkId: currentBinInfo.linkId,
      });
      return;
    }

    // Not in any bin, link directly
    handleLinkAnimal(targetBin, animal.id);
  };

  // Confirm moving animal from one bin to another
  const handleConfirmMove = async () => {
    if (!moveConfirm) return;
    setSaving(true);
    try {
      // First unlink from old bin
      await bridgeClubAPI.unlinkAnimalFromBin(moveConfirm.fromBin.id, moveConfirm.fromLinkId);
      // Then link to new bin
      await bridgeClubAPI.linkAnimalToBin(moveConfirm.toBin.id, { animal_id: moveConfirm.animal.id });
      setMoveConfirm(null);
      setShowLinkAnimal(null);
      await loadData();
    } catch (err: any) {
      console.error('Failed to move animal:', err);
      setError(err?.error || 'Failed to move animal');
    } finally {
      setSaving(false);
    }
  };

  // Delete a bin
  const handleDeleteBin = async () => {
    if (!deleteConfirm) return;
    setSaving(true);
    try {
      await bridgeClubAPI.deleteFeedBin(deleteConfirm.id);
      setDeleteConfirm(null);
      setShowBinDetail(null);
      await loadData();
    } catch (err: any) {
      console.error('Failed to delete bin:', err);
      setError(err?.error || 'Failed to delete bin');
    } finally {
      setSaving(false);
    }
  };

  // ONE PLAIN SENTENCE ABOUT THE BIN (Brad, 2026-09-22: "Most people dont have a
  // clue what to do with these"). In place of four tiles: how much is in it, how long
  // that lasts, and roughly when it runs out.
  const binSentence = (bin: FeedBin): string => {
    const parts = [`${Number(bin.current_lbs).toFixed(0)} lb in the bin`];
    if (bin.days_remaining === null) {
      parts.push(bin.animal_links.length === 0 ? 'add an animal to see how long it lasts' : 'no daily use yet');
    } else if (bin.days_remaining <= 0) {
      parts.push('empty, or about to be');
    } else {
      parts.push(`about ${bin.days_remaining} day${bin.days_remaining === 1 ? '' : 's'} left`);
      if (bin.estimated_empty_date) parts.push(`runs out around ${formatPredictionDate(bin.estimated_empty_date).text}`);
    }
    return parts.join(' · ');
  };
  const binPill = (bin: FeedBin): { text: string; bg: string; fg: string } => {
    if (bin.days_remaining === null) return { text: 'Needs an animal', bg: '#f3f4f6', fg: '#374151' };
    if (bin.days_remaining <= 0) return { text: 'Empty', bg: '#fee2e2', fg: '#7f1d1d' };
    if (bin.days_remaining <= 7) return { text: 'Running low', bg: '#fef3c7', fg: '#92400e' };
    return { text: 'OK', bg: '#dcfce7', fg: '#166534' };
  };
  const pillStyle = (p: { bg: string; fg: string }): React.CSSProperties => ({
    fontSize: 13, fontWeight: 700, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap', background: p.bg, color: p.fg,
  });

  const getCategoryEmoji = (category: string) => {
    const cat = FEED_CATEGORIES.find(c => c.value === category);
    return cat?.emoji || '🐾';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good': return '#10b981';  // Green - 30+ days
      case 'ok': return '#84cc16';     // Lime - 14-30 days
      case 'low': return '#f59e0b';    // Orange - 7-14 days
      case 'critical': return '#dc2626'; // Red - 0-7 days
      case 'empty': return '#6b7280';  // Gray - empty
      default: return '#9ca3af';       // Unknown
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPredictionDate = (dateStr: string): { text: string; isOverdue: boolean } => {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    if (compareDate < today) {
      return { text: 'Overdue!', isOverdue: true };
    }

    const formatted = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    return { text: formatted, isOverdue: false };
  };

  if (loading) {
    return (
      <BridgeClubLayout>
        <div className="bridge-club-loading">
          <div className="bridge-club-spinner"></div>
          <p>Loading feed tracking...</p>
        </div>
      </BridgeClubLayout>
    );
  }

  return (
    <BridgeClubLayout>
      <div className="feed-page">
        <div className="page-header">
          <h1>Feed Bins</h1>
          <p className="page-subtitle">Track your feed levels with smart bins</p>
        </div>

        {error && (
          <div className="error-message" onClick={() => setError(null)}>
            {error}
          </div>
        )}

        {/* Pending Allocations Banner */}
        {pendingCount > 0 && (
          <div className="pending-banner" onClick={loadPendingAllocations}>
            <span className="pending-icon">📦</span>
            <div className="pending-text">
              <strong>{pendingCount} item{pendingCount > 1 ? 's' : ''} to assign</strong>
              <span>Tap to assign recent purchases to your bins</span>
            </div>
            <span className="pending-arrow">→</span>
          </div>
        )}

        {/* Summary */}
        {bins.length > 0 && (
          <div className="bins-summary">
            <div className="summary-stat">
              <span className="stat-value">{bins.length}</span>
              <span className="stat-label">Bins</span>
            </div>
            <div className="summary-stat summary-warning">
              <span className="stat-value">
                {bins.filter(b => b.days_remaining !== null && b.days_remaining <= 7).length}
              </span>
              <span className="stat-label">Need Attention</span>
            </div>
          </div>
        )}

        {/* Create Bin Button */}
        <button className="create-bin-btn" onClick={() => setShowCreateBin(true)}>
          + Create New Bin
        </button>

        {/* Bins List */}
        {bins.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">🗑️</span>
            <h3>No Feed Bins Yet</h3>
            <p>
              Create bins to track your feed. When you scan receipts,
              you'll assign purchases to your bins.
            </p>
            <button className="btn-primary" onClick={() => setShowCreateBin(true)}>
              Create Your First Bin
            </button>
          </div>
        ) : (
          <div className="bins-grid">
            {bins.map((bin) => (
              <div
                key={bin.id}
                className={`bin-card bin-${bin.status}`}
                onClick={() => setShowBinDetail(bin)}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 17, overflowWrap: 'anywhere' }}>
                    {getCategoryEmoji(bin.feed_category)} {bin.name}
                  </div>
                  <span style={pillStyle(binPill(bin))}>{binPill(bin).text}</span>
                </div>
                <div style={{ marginTop: 6, fontSize: 15, color: '#111', overflowWrap: 'anywhere' }}>
                  {bin.current_product_name
                    ? <>{bin.current_product_name}{bin.form_type_display ? <span style={{ color: '#6b7280' }}> · {bin.form_type_display}</span> : null}</>
                    : <span style={{ color: '#b45309', fontWeight: 600 }}>No feed added yet</span>}
                </div>
                <div style={{ marginTop: 4, fontSize: 14, color: '#6b7280' }}>{binSentence(bin)}</div>
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                  {bin.animal_links.length === 0
                    ? <span style={{ fontSize: 13, color: '#6b7280' }}>No animals linked</span>
                    : bin.animal_links.map(link => (
                      <span key={link.id} className="animal-chip">{link.animal_name}</span>
                    ))}
                </div>
                <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className="btn-add-feed"
                    style={{ flex: 1, minHeight: 44, fontSize: 15, fontWeight: 700 }}
                    onClick={(e) => { e.stopPropagation(); setShowAddFeed(bin); }}
                  >
                    + Add feed
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    style={{ flex: 1, minHeight: 44 }}
                    onClick={(e) => { e.stopPropagation(); setShowBinDetail(bin); }}
                  >
                    Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tip */}
        <div className="feed-tip">
          <span className="tip-icon">💡</span>
          <p>
            {/* Brad's words (2026-09-24). */}
            <strong>How it works:</strong> A bin is created for each kind of feed. Click
            Add feed when you buy a bag. Link the animals that eat from it, and we will
            work out when it runs out.
          </p>
        </div>

        {/* Create Bin Modal */}
        {showCreateBin && (
          <CreateBinDialog
            onClose={() => setShowCreateBin(false)}
            onCreated={loadData}
            onError={setError}
          />
        )}

        {/* Add Feed Modal */}
        {showAddFeed && (
          <AddFeedDialog
            bin={showAddFeed}
            onClose={() => setShowAddFeed(null)}
            onAdded={loadData}
            onError={setError}
          />
        )}

        {/* Pending Allocations Modal */}
        {showAllocations && (
          <div className="modal-overlay" onClick={() => setShowAllocations(false)}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
              <h3>Assign Recent Purchases</h3>
              {pendingAllocations.length === 0 ? (
                <p className="no-pending">All purchases have been assigned!</p>
              ) : (
                <div className="allocations-list">
                  {pendingAllocations.map(alloc => (
                    <div key={alloc.id} className="allocation-item">
                      <div className="alloc-info">
                        <span className="alloc-product">{alloc.product_name}</span>
                        <span className="alloc-details">
                          {alloc.quantity}x {alloc.lbs_per_unit}lb = {Number(alloc.total_lbs).toFixed(0)} lbs
                          {alloc.is_red_bridge && <span className="rb-badge">Red Bridge</span>}
                        </span>
                      </div>
                      <div className="alloc-actions">
                        {alloc.suggested_bin_id && (
                          <button
                            className="btn-suggested"
                            onClick={() => handleAllocate(alloc, alloc.suggested_bin_id!)}
                            disabled={saving}
                          >
                            → {alloc.suggested_bin_name}
                          </button>
                        )}
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              handleAllocate(alloc, parseInt(e.target.value));
                            }
                          }}
                          defaultValue=""
                        >
                          <option value="">Select bin...</option>
                          {bins.map(bin => (
                            <option key={bin.id} value={bin.id}>
                              {bin.name} ({Number(bin.current_lbs).toFixed(0)} lbs)
                            </option>
                          ))}
                        </select>
                        <button
                          className="btn-new-bin"
                          onClick={() => handleCreateBinAndAllocate(alloc)}
                          disabled={saving}
                        >
                          + New Bin
                        </button>
                        <button
                          className="btn-skip"
                          onClick={() => handleSkipAllocation(alloc)}
                          disabled={saving}
                        >
                          Skip
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowAllocations(false)}>
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bin Detail Modal - THREE JOBS, NAMED (Brad, 2026-09-22: "Hard to follow what
            I need to do, how do I add feed, add a dog, change feed"). The sentence up top
            in place of four tiles; "I put feed in", "Who eats from this bin", "Change
            what we deliver"; set-the-level and history folded away; one Add Feed. */}
        {showBinDetail && (() => {
          const bin = showBinDetail;
          const pill = binPill(bin);
          const muted: React.CSSProperties = { color: '#6b7280', fontSize: 13 };
          const section: React.CSSProperties = { border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 12px', marginBottom: 10 };
          const h: React.CSSProperties = { margin: 0, fontSize: 16, fontWeight: 700, color: '#111' };
          const primary: React.CSSProperties = { width: '100%', minHeight: 44, borderRadius: 8, border: 'none', background: '#8B0000', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 15 };
          const secondary: React.CSSProperties = { width: '100%', minHeight: 44, borderRadius: 8, border: '1px solid #8B0000', background: '#fff', color: '#8B0000', fontWeight: 600, cursor: 'pointer', fontSize: 15 };
          const fold: React.CSSProperties = { background: 'none', border: 'none', padding: '10px 0', minHeight: 44, color: '#8B0000', fontWeight: 600, fontSize: 14, cursor: 'pointer', textAlign: 'left', width: '100%' };
          return (
            <div
              className="modal-overlay"
              onClick={() => { setShowBinDetail(null); setShowAdjust(false); setShowHistory(false); setRenaming(null); }}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 16px 100px' }}
            >
              <div
                className="modal-content modal-large"
                onClick={(e) => e.stopPropagation()}
                style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: 'calc(100vh - 140px)', padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
              >
                <div style={{ padding: '12px 16px 10px', borderBottom: '1px solid #eee', flex: '0 0 auto' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {renaming === null ? (
                      <h3 style={{ margin: 0, fontSize: 18, color: '#8B0000', overflowWrap: 'anywhere', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span>{getCategoryEmoji(bin.feed_category)} {bin.name}</span>
                        <button
                          type="button"
                          aria-label="Rename this bin"
                          title="Rename"
                          onClick={() => setRenaming(bin.name)}
                          style={{ background: 'none', border: 'none', padding: '0 4px', minHeight: 32, cursor: 'pointer', fontSize: 16, color: '#6b7280' }}
                        >
                          ✎
                        </button>
                      </h3>
                    ) : (
                      // THE FIELD FULL WIDTH, THE BUTTONS UNDER IT (Brad, 2026-09-23: "The edit
                      // box is really small"). Three things on one row left it three words wide.
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <input
                          type="text"
                          value={renaming}
                          autoFocus
                          aria-label="Bin name"
                          onChange={(e) => setRenaming(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleRename(bin); } if (e.key === 'Escape') setRenaming(null); }}
                          style={{ width: '100%', boxSizing: 'border-box', minHeight: 44, fontSize: 18, fontWeight: 600, padding: '0 12px', borderRadius: 8, border: '1px solid #d1d5db' }}
                        />
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button type="button" className="btn-primary" style={{ flex: 1, minHeight: 44 }} disabled={saving || !(renaming || '').trim()} onClick={() => handleRename(bin)}>Save</button>
                          <button type="button" className="btn-secondary" style={{ flex: 1, minHeight: 44 }} disabled={saving} onClick={() => setRenaming(null)}>Cancel</button>
                        </div>
                      </div>
                    )}
                    {renaming === null && <span style={pillStyle(pill)}>{pill.text}</span>}
                  </div>
                  {bin.current_product_name && (
                    <div style={{ fontSize: 15, marginTop: 4, overflowWrap: 'anywhere' }}>
                      {bin.current_product_name}{bin.form_type_display ? <span style={{ color: '#6b7280' }}> · {bin.form_type_display}</span> : null}
                    </div>
                  )}
                  <div style={{ ...muted, fontSize: 14, marginTop: 2 }}>{binSentence(bin)}</div>
                </div>

                <div style={{ padding: '12px 16px', overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                  {/* 1. I put feed in */}
                  <div style={section}>
                    <div style={h}>Bought a bag?</div>
                    <p style={{ ...muted, margin: '4px 0 10px' }}>Add it, so we know what is in the bin and how much.</p>
                    <button type="button" style={primary} onClick={() => { setShowBinDetail(null); setShowAddFeed(bin); }}>
                      + Add feed
                    </button>
                  </div>

                  {/* 2. Who eats from this bin */}
                  <div style={section}>
                    <div style={h}>Who eats from this bin?</div>
                    <p style={{ ...muted, margin: '4px 0 10px' }}>This is how we work out when it runs out.</p>
                    {bin.animal_links.length === 0 ? (
                      <p style={{ margin: '0 0 10px', fontSize: 14, color: '#b45309', fontWeight: 600 }}>No animals yet.</p>
                    ) : (
                      <div style={{ display: 'grid', gap: 6, marginBottom: 10 }}>
                        {bin.animal_links.map(link => (
                          <div key={link.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: '1px solid #f3f4f6' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: 15 }}>{link.animal_name}</div>
                              <div style={{ ...muted, fontSize: 13 }}>
                                {link.use_custom_amount
                                  ? `${link.custom_amount_per_day} ${link.custom_amount_unit} a day, your amount (${(link.effective_daily_lbs ?? 0).toFixed(1)} lb)`
                                  : `about ${(link.system_estimate_lbs ?? 0).toFixed(1)} lb a day, our estimate`}
                                {link.feeding_notes ? ` · ${link.feeding_notes}` : ''}
                              </div>
                            </div>
                            <button type="button" className="btn-edit-feeding" style={{ minHeight: 40 }} onClick={() => openEditFeedingModal(bin, link)}>
                              {link.use_custom_amount ? 'Edit' : 'Set amount'}
                            </button>
                            <button type="button" className="btn-unlink" style={{ minHeight: 40 }} onClick={() => handleUnlinkAnimal(bin, link.id)}>
                              Unlink
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <button type="button" style={secondary} onClick={() => setShowLinkAnimal(bin)}>
                      + Add an animal
                    </button>
                  </div>

                  {/* 3. Change what we deliver */}
                  <div style={section}>
                    <div style={h}>Want a different feed delivered?</div>
                    <p style={{ ...muted, margin: '4px 0 10px' }}>Change it on your Subscription page. Nothing is added to the bin.</p>
                    {/* Straight into the dialog for THIS bin, with Back returning here
                        (Brad, 2026-09-22: "The user is already wanting the change. Just
                        push them forward further."). DeliveryPage reads ?change and ?from. */}
                    <Link to={`/bridge-club/delivery?change=${bin.id}&from=feed`} style={{ ...secondary, display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                      Change what we deliver →
                    </Link>
                  </div>

                  {/* Folded away: set the level, history */}
                  <button type="button" style={fold} onClick={() => setShowAdjust((v) => !v)}>
                    {showAdjust ? '▾' : '▸'} Bin doesn't match? Set the level
                  </button>
                  {showAdjust && (
                    <div className="adjust-form" style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={adjustLevel}
                        onChange={(e) => setAdjustLevel(e.target.value)}
                        onBlur={(e) => { const val = parseFloat(e.target.value) || 0; setAdjustLevel(val >= 0 ? String(val) : ''); }}
                        placeholder="What's in it now (lb)"
                        aria-label="New level (lb)"
                        style={{ flex: 1, minHeight: 44, fontSize: 16, padding: '0 10px', borderRadius: 8, border: '1px solid #d1d5db' }}
                      />
                      <button type="button" className="btn-primary" style={{ minHeight: 44 }} onClick={() => handleAdjustLevel(bin)} disabled={saving || !adjustLevel}>
                        {saving ? '...' : 'Set'}
                      </button>
                    </div>
                  )}
                  {bin.recent_transactions.length > 0 && (
                    <>
                      <button type="button" style={fold} onClick={() => setShowHistory((v) => !v)}>
                        {showHistory ? '▾' : '▸'} History
                      </button>
                      {showHistory && (
                        <div className="activity-list">
                          {bin.recent_transactions.map(tx => (
                            <div key={tx.id} className="activity-item">
                              <span className={`activity-change ${tx.lbs_change >= 0 ? 'positive' : 'negative'}`}>
                                {tx.lbs_change >= 0 ? '+' : ''}{Number(tx.lbs_change).toFixed(1)} lb
                              </span>
                              <span className="activity-desc">{tx.product_name || tx.type}{tx.brand && ` (${tx.brand})`}</span>
                              <span className="activity-date">{formatDate(tx.created_at)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  <div style={{ textAlign: 'center', marginTop: 8 }}>
                    <button type="button" className="btn-delete-text" style={{ minHeight: 44 }} onClick={() => setDeleteConfirm(bin)}>
                      Delete this bin
                    </button>
                  </div>
                </div>

                <div style={{ padding: '10px 16px 14px', borderTop: '1px solid #eee', flex: '0 0 auto' }}>
                  <button type="button" className="btn-secondary" style={{ width: '100%', minHeight: 44 }}
                    onClick={() => { setShowBinDetail(null); setShowAdjust(false); setShowHistory(false); setRenaming(null); }}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Link Animal Modal */}
        {showLinkAnimal && (
          <div className="modal-overlay" style={{ zIndex: 2100 }} onClick={() => setShowLinkAnimal(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Link Animal to {showLinkAnimal.name}</h3>
              {animals.length === 0 ? (
                <p className="no-animals">
                  No animals in your profile. Add animals in the Animals section first.
                </p>
              ) : (
                <div className="animal-select-list">
                  {animals.map(animal => {
                    const currentBinInfo = getAnimalCurrentBin(animal.id);
                    const isInThisBin = currentBinInfo?.bin.id === showLinkAnimal.id;
                    const isInOtherBin = currentBinInfo && !isInThisBin;

                    return (
                      <button
                        key={animal.id}
                        className={`animal-select-btn ${isInThisBin ? 'already-linked' : ''} ${isInOtherBin ? 'in-other-bin' : ''}`}
                        onClick={() => !isInThisBin && handleAnimalClick(animal, showLinkAnimal)}
                        disabled={saving || isInThisBin}
                      >
                        <div className="animal-select-left">
                          {isInThisBin && <span className="check-icon">✓</span>}
                          <span className="animal-emoji">{getCategoryEmoji(animal.category)}</span>
                          <span className="animal-name">{animal.name || animal.animal_type}</span>
                        </div>
                        <span className="animal-bin-info">
                          {isInThisBin && '(already linked)'}
                          {isInOtherBin && `(in ${currentBinInfo.bin.name})`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowLinkAnimal(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Move Animal Confirmation Modal */}
        {moveConfirm && (
          <div className="modal-overlay" style={{ zIndex: 2100 }} onClick={() => setMoveConfirm(null)}>
            <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
              <h3>Move Animal?</h3>
              <p className="confirm-message">
                <strong>{moveConfirm.animal.name || moveConfirm.animal.animal_type}</strong> is currently linked to <strong>{moveConfirm.fromBin.name}</strong>.
              </p>
              <p className="confirm-message">
                Move to <strong>{moveConfirm.toBin.name}</strong>?
              </p>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setMoveConfirm(null)}>
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleConfirmMove}
                  disabled={saving}
                >
                  {saving ? 'Moving...' : 'Move Animal'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Bin Confirmation Modal */}
        {deleteConfirm && (
          <div className="modal-overlay" style={{ zIndex: 2100 }} onClick={() => setDeleteConfirm(null)}>
            <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Bin?</h3>
              <p className="confirm-message">
                Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?
              </p>
              <p className="confirm-warning">
                This will remove all feed history and unlink all animals from this bin.
              </p>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setDeleteConfirm(null)}>
                  Cancel
                </button>
                <button
                  className="btn-danger"
                  onClick={handleDeleteBin}
                  disabled={saving}
                >
                  {saving ? 'Deleting...' : 'Delete Bin'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Custom Feeding Amount Modal */}
        {editFeedingLink && (
          <div className="modal-overlay" style={{ zIndex: 2100 }} onClick={() => setEditFeedingLink(null)}>
            <div className="modal-content modal-feeding" onClick={(e) => e.stopPropagation()}>
              <h3>Feeding Amount: {editFeedingLink.link.animal_name}</h3>
              <p className="feeding-bin-context">
                Bin: {editFeedingLink.bin.name}
              </p>

              {/* Toggle: Custom vs System Estimate */}
              <div className="feeding-toggle-section">
                <label className="toggle-option">
                  <input
                    type="radio"
                    name="feedingType"
                    checked={!feedingUseCustom}
                    onChange={() => setFeedingUseCustom(false)}
                  />
                  <span className="toggle-label">
                    Use System Estimate
                    <span className="toggle-value">
                      ({(editFeedingLink.link.system_estimate_lbs ?? 0).toFixed(2)} lbs/day)
                    </span>
                  </span>
                </label>
                <label className="toggle-option">
                  <input
                    type="radio"
                    name="feedingType"
                    checked={feedingUseCustom}
                    onChange={() => setFeedingUseCustom(true)}
                  />
                  <span className="toggle-label">Set My Own Amount</span>
                </label>
              </div>

              {/* Custom Amount Fields (only shown when custom is selected) */}
              {feedingUseCustom && (
                <div className="custom-amount-section">
                  <div className="form-row">
                    <div className="form-group amount-input">
                      <label>Amount Per Day</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={feedingAmount}
                        onChange={(e) => setFeedingAmount(e.target.value)}
                        placeholder="e.g., 2"
                      />
                    </div>
                    <div className="form-group unit-select">
                      <label>Unit</label>
                      <select
                        value={feedingUnit}
                        onChange={(e) => setFeedingUnit(e.target.value)}
                      >
                        <option value="cups">Cups</option>
                        <option value="lbs">Pounds</option>
                        <option value="oz">Ounces</option>
                        <option value="scoops">Scoops</option>
                        <option value="grams">Grams</option>
                        <option value="kg">Kilograms</option>
                      </select>
                    </div>
                  </div>

                  {/* Scoop Size (only for scoops) */}
                  {feedingUnit === 'scoops' && (
                    <div className="form-group scoop-size">
                      <label>Scoop Size (oz)</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={feedingScoopSize}
                        onChange={(e) => setFeedingScoopSize(e.target.value)}
                        placeholder="e.g., 8 (default)"
                      />
                      <span className="field-hint">Leave blank for 8 oz default</span>
                    </div>
                  )}

                  {/* Real-time Preview */}
                  <div className="lbs-preview">
                    <span className="preview-label">Calculated daily consumption:</span>
                    <span className="preview-value">{getPreviewLbs().toFixed(2)} lbs/day</span>
                  </div>
                </div>
              )}

              {/* Feeding Notes */}
              <div className="form-group">
                <label>Feeding Notes (optional)</label>
                <textarea
                  value={feedingNotes}
                  onChange={(e) => setFeedingNotes(e.target.value)}
                  placeholder="e.g., 1 cup morning, 1 cup evening"
                  rows={2}
                />
              </div>

              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setEditFeedingLink(null)}>
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleSaveFeeding}
                  disabled={saving || (feedingUseCustom && !feedingAmount)}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </BridgeClubLayout>
  );
};

export default BridgeClubFeed;
